import { SyncStatus } from '../models/widget-state';

import { IconButton, Theme, Tooltip, useMediaQuery } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FilterListIcon from '@mui/icons-material/FilterList';
import RedoIcon from '@mui/icons-material/Redo';
import styled from 'styled-components';
import UndoIcon from '@mui/icons-material/Undo';

export interface TopBarProps {
  isLocked: boolean;
  filtersCount: number;
  numTotalPapers: number;
  numFilteredPapers: number;
  syncStatus: SyncStatus;
  undoDisabled?: boolean;
  onUndoClicked?: () => void;
  redoDisabled?: boolean;
  onRedoClicked?: () => void;
  onExportAllClicked?: () => void;
  onOpenFacetsDrawerClicked?: () => void;
}

export function TopBar(props: TopBarProps): JSX.Element {
  const {
    isLocked,
    syncStatus,
    filtersCount,
    numTotalPapers,
    numFilteredPapers,
    undoDisabled = false,
    onUndoClicked = () => {},
    redoDisabled = false,
    onRedoClicked = () => {},
    onExportAllClicked = () => {},
    onOpenFacetsDrawerClicked = () => {},
  } = props;
  const isSmallScreen = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down('md'),
    { noSsr: true },
  );
  const papers = numTotalPapers === 1 ? 'Paper' : 'Papers';

  const hasFilters = filtersCount > 0;
  const ratioOfPapers = hasFilters
    ? `${numFilteredPapers.toLocaleString()} / ${numTotalPapers.toLocaleString()}`
    : `${numTotalPapers.toLocaleString()}`;
  const numberOfPapers = `${ratioOfPapers} ${papers}`;
  const exportLabel =
    numFilteredPapers === numTotalPapers ? 'All' : `${ratioOfPapers}`;

  return (
    <BarLayout>
      {!isSmallScreen && (
        <HistoryDiv>
          <Tooltip title="Undo">
            <DisableTooltipWrapper>
              <HistoryButton onClick={onUndoClicked} disabled={undoDisabled}>
                <UndoIcon />
              </HistoryButton>
            </DisableTooltipWrapper>
          </Tooltip>
          <Tooltip title="Redo">
            <DisableTooltipWrapper>
              <HistoryButton onClick={onRedoClicked} disabled={redoDisabled}>
                <RedoIcon />
              </HistoryButton>
            </DisableTooltipWrapper>
          </Tooltip>
        </HistoryDiv>
      )}
      <Tooltip placement="bottom" title={syncMessages[syncStatus.type]}>
        <SyncIcon status={syncStatus.type} />
      </Tooltip>
      {!isLocked && numTotalPapers > 0 && (
        <NumPapersSpan>{numberOfPapers}</NumPapersSpan>
      )}
      <Grow />
      {!isLocked && !isSmallScreen && numTotalPapers > 0 && (
        <Tooltip title="Download">
          <ExportButton onClick={onExportAllClicked}>
            <DownloadIcon />
            Export {exportLabel} Citatons
          </ExportButton>
        </Tooltip>
      )}
      {!isLocked && isSmallScreen && numTotalPapers > 0 && (
        <FilterDiv>
          {hasFilters && (
            <Tooltip title={`${filtersCount} active filters`}>
              <FilterCount>{filtersCount}</FilterCount>
            </Tooltip>
          )}
          <IconButton onClick={onOpenFacetsDrawerClicked}>
            <FilterListIcon />
          </IconButton>
        </FilterDiv>
      )}
    </BarLayout>
  );
}
const BarLayout = styled.div`
  display: flex;
  width: 100%;
  height: 56px;
  justify-content: start;
  align-items: center;
  padding: 0px 16px;
  border-bottom: 1px solid rgba(250, 242, 233, 0.1);
`;

const FilterCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 42px;
  font-size: 12px;
  font-weight: 500;
  color: black;
  position: absolute;
  user-select: none;
  white-space: nowrap;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.darkCategoricalColor.Orange.hex};
`;

const FilterDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const HistoryDiv = styled.div`
  display: flex;
  padding: 0px 10px;
  gap: 2px;
`;

const HistoryButton = styled(IconButton)`
  &.MuiIconButton-root {
    padding: 0;
    margin: 4px;
  }
`;

// NOTE: This wrapper is required for disabled buttons to show tooltips
const DisableTooltipWrapper = styled.span`
  display: 'inline-block';
`;

const NumPapersSpan = styled.span`
  font-size: 18px;
  font-weight: 500;
  margin-left: 10px;
  user-select: none;
  white-space: nowrap;
`;

const Grow = styled.div`
  flex-grow: 1;
`;

const ExportButton = styled(IconButton)`
  padding: 0;
  &.MuiButtonBase-root .MuiSvgIcon-root {
    margin-right: 4px;
    width: 18px;
    height: 18px;
  }
`;

const syncMessages = {
  ok: 'synchronized',
  reconnecting: 'Communication Error, attempting to re-connect',
  error: 'Communication Error, disconnected from server',
};

const SyncIcon = styled(FiberManualRecordIcon)<{
  status: 'ok' | 'error' | string;
}>`
  color: ${({ status, theme }) =>
    status === 'ok'
      ? `${theme.darkCategoricalColor.Green.hex}`
      : status === 'error'
      ? `${theme.darkCategoricalColor.Red.hex}`
      : `${theme.darkCategoricalColor.Orange.hex}`};
  flex: ${({ status }) => (status === 'ok' ? '0' : 'none')};
`;
